// Customer portal — Bookings section.
//
// Lists the customer's existing bookings (showroom visit, home visit,
// site survey, callback, design consultation) and exposes a "Request
// new" form that POSTs to the retailer's appointment-requests queue.
//
// Source-of-truth references (READ-ONLY):
//   - customer-standalone.html:799-809   page-appointments panel
//   - customer-live.js:760-796            renderAppointments
//
// 2026-05-20 visual upgrade — replaced the simple list view with the
// source-of-truth calendar-block layout: each booking shows a day/num/
// month stack, status pill (confirmed / completed / pending), and
// per-item actions (View notes for past visits, Reschedule + Add to
// calendar for upcoming). Past visits drop the "next" stripe.
//
// Backend
// -------
//   POST /api/customer/appointments/request
//     body: { visitType, preferredDate, preferredTime, notes }
//     returns: { ok, appointment }

import { ApiError } from "@tkos/api-client";

import type {
  CustomerBooking,
  CustomerProject,
  CustomerSection,
  SectionCleanup,
  SectionContext,
} from "../types";
import { formatAddress } from "../types";

import "./bookings.css";

// ── Visit type catalogue ────────────────────────────────────────────────────

interface VisitTypeMeta {
  value: string;
  label: string;
  description: string;
}

const VISIT_TYPES: readonly VisitTypeMeta[] = [
  {
    value: "showroom",
    label: "Showroom visit",
    description: "Come and see ranges, samples and finishes in person.",
  },
  {
    value: "home_visit",
    label: "Home visit",
    description: "We come to you for a measure-up or a design conversation.",
  },
  {
    value: "design_consultation",
    label: "Design consultation",
    description: "Sit down with a designer (in-showroom or video call).",
  },
  {
    value: "site_survey",
    label: "Site survey",
    description: "Pre-install survey — services, access, measurements.",
  },
  {
    value: "callback",
    label: "Call me back",
    description: "Quick phone callback at a time that suits you.",
  },
];

const VISIT_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  VISIT_TYPES.map((v) => [v.value, v.label]),
);

function visitTypeLabel(raw: string): string {
  return VISIT_LABEL_BY_VALUE[raw] ?? humanise(raw);
}

function humanise(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Formatters ─────────────────────────────────────────────────────────────

const APPT_DAY_FMT = new Intl.DateTimeFormat("en-GB", { weekday: "short" });
const APPT_MON_FMT = new Intl.DateTimeFormat("en-GB", { month: "short" });

function formatTime(hhmm: string | null): string {
  if (!hhmm) return "";
  return hhmm.replace(/^([0-9]{2}):([0-9]{2}).*$/, "$1:$2");
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

/** Resolve the appointment moment — prefer server-confirmed scheduledAt,
 *  else fall back to (preferredDate + preferredTime) combined. */
function apptDate(b: CustomerBooking): Date | null {
  const candidate = b.scheduledAt
    ? Date.parse(b.scheduledAt)
    : Date.parse(`${b.preferredDate}T${b.preferredTime ?? "09:00"}`);
  if (!Number.isFinite(candidate)) {
    const fallback = Date.parse(b.preferredDate);
    if (!Number.isFinite(fallback)) return null;
    return new Date(fallback);
  }
  return new Date(candidate);
}

// ── Inline SVG icons ────────────────────────────────────────────────────────

const ICON_CLOCK = '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>';
const ICON_PIN = '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>';
const ICON_USERS = '<path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>';
const ICON_PLUS = '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>';

function svg(path: string, size = 13): string {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
}

// ── Booking card rendering ──────────────────────────────────────────────────

function renderBookingCard(
  b: CustomerBooking,
  retailerName: string,
  customerAddress: string,
): string {
  const d = apptDate(b);
  const day = d ? APPT_DAY_FMT.format(d) : "";
  const num = d ? String(d.getDate()) : "";
  const mon = d ? APPT_MON_FMT.format(d) : "";
  const now = Date.now();
  const past = d != null && d.getTime() < now;

  // A PENDING appointment stays pending even if its preferred date has passed —
  // the customer still hasn't confirmed it, so it must keep its Confirm button
  // (Issue 2: "Confirm button missing"). Only already-actioned bookings roll
  // over to "completed" once their date is in the past.
  const statusValue =
    past && b.status !== "cancelled" && b.status !== "pending" ? "completed" : b.status;
  const statusCls =
    statusValue === "confirmed"
      ? "cust-pill-status brand"
      : statusValue === "completed"
        ? "cust-pill-status green"
        : statusValue === "cancelled"
          ? "cust-pill-status red"
          : "cust-pill-status blue";
  const statusLabel =
    statusValue === "confirmed"
      ? "Confirmed"
      : statusValue === "completed"
        ? "Completed"
        : statusValue === "cancelled"
          ? "Cancelled"
          : statusValue === "pending"
            ? "Pending"
            : humanise(statusValue);

  const time = formatTime(b.preferredTime);
  const location =
    b.visitType === "showroom"
      ? `${retailerName} showroom`
      : b.visitType === "home_visit" || b.visitType === "site_survey"
        ? "Your address"
        : b.visitType === "callback"
          ? "Phone call"
          : retailerName;
  // Calendar-event location: use the customer's real saved address for home /
  // site visits (the card only shows the generic "Your address"); everything
  // else reuses the displayed location (showroom / phone call / retailer).
  const calLocation =
    (b.visitType === "home_visit" || b.visitType === "site_survey") && customerAddress
      ? customerAddress
      : location;

  const actions = (past && statusValue !== "pending")
    ? `<button type="button" class="bk-action" data-action="view-notes" data-id="${esc(b.id)}">View notes</button>`
    : `${statusValue === "pending"
        ? `<button type="button" class="bk-action bk-action--primary" data-action="confirm" data-id="${esc(b.id)}">Confirm</button>`
        : ""}<button type="button" class="bk-action" data-action="reschedule" data-id="${esc(b.id)}">Reschedule</button>`;

  // One-tap "Add to Calendar" links — only for confirmed (firm) bookings.
  const calendarRow =
    statusValue === "confirmed"
      ? calendarLinksHtml(b, retailerName, calLocation)
      : "";

  return `
    <div class="bk-card${past ? "" : " bk-card--next"}" data-booking-id="${esc(b.id)}">
      <div class="bk-date">
        <div class="bk-date-day">${esc(day)}</div>
        <div class="bk-date-num">${esc(num)}</div>
        <div class="bk-date-mon">${esc(mon)}</div>
      </div>
      <div class="bk-info">
        <div class="bk-title-row">
          <span class="bk-title">${esc(visitTypeLabel(b.visitType))}</span>
          <span class="${statusCls}">${esc(statusLabel)}</span>
        </div>
        <div class="bk-meta">
          ${time ? `<span>${svg(ICON_CLOCK)}${esc(time)}</span>` : ""}
          <span>${svg(ICON_PIN)}${esc(location)}</span>
          <span>${svg(ICON_USERS)}${esc(retailerName)}</span>
        </div>
        ${b.notes ? `<div class="bk-notes">${esc(b.notes)}</div>` : ""}
        ${calendarRow}
      </div>
      <div class="bk-actions">${actions}</div>
    </div>
  `;
}

function groupAndRender(
  bookings: CustomerBooking[],
  retailerName: string,
  customerAddress: string,
): string {
  if (bookings.length === 0) {
    return `<div class="bk-empty">
      <div class="bk-empty-title">No appointments yet</div>
      <div class="bk-empty-body">Your retailer will schedule showroom and home visits with you here.</div>
    </div>`;
  }

  const now = Date.now();
  const upcoming: CustomerBooking[] = [];
  const past: CustomerBooking[] = [];
  for (const b of bookings) {
    const d = apptDate(b);
    if (d && d.getTime() < now) past.push(b);
    else upcoming.push(b);
  }
  // Soonest first; past most recent first.
  upcoming.sort((a, b) => {
    const ad = apptDate(a)?.getTime() ?? 0;
    const bd = apptDate(b)?.getTime() ?? 0;
    return ad - bd;
  });
  past.sort((a, b) => {
    const ad = apptDate(a)?.getTime() ?? 0;
    const bd = apptDate(b)?.getTime() ?? 0;
    return bd - ad;
  });

  const upcomingHtml = upcoming.length
    ? `<div class="bk-group-label">Upcoming</div>
       <div class="bk-list">${upcoming.map((b) => renderBookingCard(b, retailerName, customerAddress)).join("")}</div>`
    : "";
  const pastHtml = past.length
    ? `<div class="bk-group-label">Past</div>
       <div class="bk-list">${past.map((b) => renderBookingCard(b, retailerName, customerAddress)).join("")}</div>`
    : "";
  return upcomingHtml + pastHtml;
}

// ── Request-form rendering ──────────────────────────────────────────────────

function tomorrowIsoDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function renderFormHtml(): string {
  const tomorrow = tomorrowIsoDate();
  return `
    <form class="bk-form" data-role="form" novalidate>
      <div class="bk-form-row">
        <label class="bk-field">
          <span class="bk-field-label">Visit type</span>
          <select class="bk-input" name="visitType" required>
            ${VISIT_TYPES.map(
              (v) =>
                `<option value="${esc(v.value)}">${esc(v.label)}</option>`,
            ).join("")}
          </select>
          <span class="bk-field-hint" data-role="type-hint">${esc(VISIT_TYPES[0]!.description)}</span>
        </label>
      </div>
      <div class="bk-form-row bk-form-row--split">
        <label class="bk-field">
          <span class="bk-field-label">Preferred date</span>
          <input class="bk-input" type="date" name="preferredDate" required min="${esc(tomorrow)}" />
        </label>
        <label class="bk-field">
          <span class="bk-field-label">Preferred time</span>
          <input class="bk-input" type="time" name="preferredTime" />
        </label>
      </div>
      <label class="bk-field">
        <span class="bk-field-label">Notes (optional)</span>
        <textarea
          class="bk-input bk-textarea"
          name="notes"
          rows="3"
          maxlength="1000"
          placeholder="Anything we should know — parking, access, project goals…"
        ></textarea>
      </label>
      <div class="bk-form-actions">
        <button type="submit" class="cust-btn" data-role="submit">Request booking</button>
      </div>
    </form>
  `;
}

function renderCardHtml(project: CustomerProject): string {
  const retailerName = project.retailer.businessName || "Your retailer";
  // The customer's saved address (for home/site-visit calendar locations).
  // formatAddress returns "—" when unknown → treat as empty. Append the
  // separately-stored postcode only when it isn't already in the string.
  const rawAddr = formatAddress(project.customer.address);
  const addr = rawAddr === "—" ? "" : rawAddr;
  const pc = project.customer.postcode ? project.customer.postcode.trim() : "";
  const customerAddress = [addr, pc && !addr.includes(pc) ? pc : ""]
    .filter((s) => s && s.trim())
    .join(", ");
  return `
    <section class="cust-card bk-card-wrap" aria-label="Bookings">
      <header class="bk-head">
        <div>
          <h2 class="cust-card-title bk-head-title">Appointments</h2>
          <p class="bk-head-sub">Upcoming showroom visits, home measurements and design reviews.</p>
        </div>
        <button type="button" class="cust-btn bk-head-btn" data-role="toggle-form">
          ${svg(ICON_PLUS, 14)}<span>Request appointment</span>
        </button>
      </header>
      <div class="bk-content" data-role="list">${groupAndRender(project.bookings, retailerName, customerAddress)}</div>
      <div class="bk-form-panel" data-role="form-panel" hidden>
        <h3 class="bk-form-heading">Request a new booking</h3>
        ${renderFormHtml()}
      </div>
    </section>
  `;
}

// ── Calendar (.ics) helpers ─────────────────────────────────────────────────

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function toIcsTime(d: Date): string {
  return (
    d.getUTCFullYear().toString() +
    pad2(d.getUTCMonth() + 1) +
    pad2(d.getUTCDate()) +
    "T" +
    pad2(d.getUTCHours()) +
    pad2(d.getUTCMinutes()) +
    "00Z"
  );
}

/** Escape a text value for an ICS field (RFC 5545): backslash, semicolon,
 *  comma and newlines. Critical for LOCATION — addresses contain commas,
 *  which are value separators in ICS and would otherwise corrupt the event. */
function icsEscape(s: string): string {
  return String(s ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

/** Shared event model for all three calendar targets. Null when the booking
 *  has no resolvable date. Duration defaults to 1h (bookings carry no end). */
function calendarEvent(
  b: CustomerBooking,
  retailerName: string,
  location: string,
): { start: Date; end: Date; title: string; details: string; location: string } | null {
  const d = apptDate(b);
  if (!d) return null;
  const title = `${visitTypeLabel(b.visitType)} — ${retailerName}`;
  const details = [
    `${visitTypeLabel(b.visitType)} with ${retailerName}.`,
    b.notes && b.notes.trim() ? b.notes.trim() : "",
  ].filter(Boolean).join("\n\n");
  return { start: d, end: new Date(d.getTime() + 60 * 60 * 1000), title, details, location };
}

function buildIcs(b: CustomerBooking, retailerName: string, location: string): string {
  const ev = calendarEvent(b, retailerName, location);
  if (!ev) return "";
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//TKOS//Customer Portal//EN",
    "BEGIN:VEVENT",
    `UID:${b.id}@tkos`,
    `DTSTAMP:${toIcsTime(new Date())}`,
    `DTSTART:${toIcsTime(ev.start)}`,
    `DTEND:${toIcsTime(ev.end)}`,
    `SUMMARY:${icsEscape(ev.title)}`,
    `DESCRIPTION:${icsEscape(ev.details)}`,
    `LOCATION:${icsEscape(ev.location)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

/** Three one-tap "Add to Calendar" links (Google | Apple | Outlook). Google &
 *  Outlook are pre-filled deep-links; Apple is a data:text/calendar URI that
 *  opens the native Calendar app directly on iPhone/Mac (no Files detour). */
function calendarLinksHtml(
  b: CustomerBooking,
  retailerName: string,
  location: string,
): string {
  const ev = calendarEvent(b, retailerName, location);
  if (!ev) return "";
  const startUtc = toIcsTime(ev.start); // YYYYMMDDTHHmmSSZ (UTC basic)
  const endUtc = toIcsTime(ev.end);
  const enc = encodeURIComponent;

  const google =
    "https://calendar.google.com/calendar/render?action=TEMPLATE" +
    `&text=${enc(ev.title)}` +
    `&dates=${startUtc}/${endUtc}` +
    `&details=${enc(ev.details)}` +
    `&location=${enc(ev.location)}`;

  const outlook =
    "https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent" +
    `&subject=${enc(ev.title)}` +
    `&startdt=${enc(ev.start.toISOString())}` +
    `&enddt=${enc(ev.end.toISOString())}` +
    `&body=${enc(ev.details)}` +
    `&location=${enc(ev.location)}`;

  const apple = `data:text/calendar;charset=utf-8,${enc(buildIcs(b, retailerName, location))}`;

  return `
    <div class="bk-cal">
      <span class="bk-cal-label">Add to Calendar:</span>
      <a class="bk-cal-link" href="${esc(google)}" target="_blank" rel="noopener">Google</a>
      <span class="bk-cal-sep" aria-hidden="true">|</span>
      <a class="bk-cal-link" href="${esc(apple)}">Apple</a>
      <span class="bk-cal-sep" aria-hidden="true">|</span>
      <a class="bk-cal-link" href="${esc(outlook)}" target="_blank" rel="noopener">Outlook</a>
    </div>`;
}

// ── Section ────────────────────────────────────────────────────────────────

const section: CustomerSection = {
  id: "bookings",
  label: "Bookings",

  shouldRender(_project: CustomerProject): boolean {
    // Always renderable — the section IS the request-new form when empty.
    return true;
  },

  mount(container: HTMLElement, ctx: SectionContext): SectionCleanup {
    const ac = new AbortController();
    let disposed = false;

    function render(): void {
      container.innerHTML = renderCardHtml(ctx.project);
      wireToggle();
      wireForm();
    }

    function wireToggle(): void {
      const btn = container.querySelector<HTMLButtonElement>(
        "[data-role='toggle-form']",
      );
      const panel = container.querySelector<HTMLElement>(
        "[data-role='form-panel']",
      );
      if (!btn || !panel) return;
      btn.addEventListener("click", () => {
        const hidden = panel.hasAttribute("hidden");
        if (hidden) panel.removeAttribute("hidden");
        else panel.setAttribute("hidden", "");
        btn.setAttribute("aria-expanded", hidden ? "true" : "false");
      });
    }

    function wireForm(): void {
      const form = container.querySelector<HTMLFormElement>(
        "[data-role='form']",
      );
      if (!form) return;
      const submitBtn = form.querySelector<HTMLButtonElement>(
        "[data-role='submit']",
      );
      const typeSel = form.querySelector<HTMLSelectElement>(
        "select[name='visitType']",
      );
      const typeHint = form.querySelector<HTMLElement>(
        "[data-role='type-hint']",
      );

      if (typeSel && typeHint) {
        typeSel.addEventListener("change", () => {
          const meta = VISIT_TYPES.find((v) => v.value === typeSel.value);
          typeHint.textContent = meta?.description ?? "";
        });
      }

      form.addEventListener("submit", (e) => {
        e.preventDefault();
        if (disposed) return;
        const fd = new FormData(form);
        const visitType = String(fd.get("visitType") ?? "").trim();
        const preferredDate = String(fd.get("preferredDate") ?? "").trim();
        const preferredTime = String(fd.get("preferredTime") ?? "").trim();
        const notes = String(fd.get("notes") ?? "").trim().slice(0, 1000);

        if (!preferredDate) {
          ctx.showToast("Please pick a preferred date.", "error");
          return;
        }

        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = "Sending…";
        }

        void (async () => {
          try {
            await ctx.api<unknown>("/customer/appointments/request", {
              method: "POST",
              signal: ac.signal,
              body: JSON.stringify({
                // Send the canonical enum value (not the label) so the server
                // stores it consistently and the portal renders it correctly.
                visitType,
                preferredDate,
                preferredTime: preferredTime || undefined,
                notes: notes || undefined,
              }),
            });

            ctx.showToast(
              `Booking request sent — ${ctx.project.retailer.businessName || "your retailer"} will confirm shortly.`,
              "success",
            );
            form.reset();
            await ctx.refresh();
            if (!disposed) render();
          } catch (err) {
            if (isAbortError(err)) return;
            if (err instanceof ApiError && err.status === 404) {
              ctx.showToast(
                "Booking endpoint isn't live yet — your retailer has been notified by email.",
                "info",
              );
              form.reset();
            } else {
              console.error("[bookings] submit failed", err);
              ctx.showToast(
                "Couldn't send the booking request — please try again.",
                "error",
              );
            }
          } finally {
            if (!disposed && submitBtn) {
              submitBtn.disabled = false;
              submitBtn.textContent = "Request booking";
            }
          }
        })();
      });
    }

    // Per-item action handler.
    const onActionClick = (e: MouseEvent): void => {
      const target = (e.target as HTMLElement | null)?.closest<HTMLElement>(
        "[data-action]",
      );
      if (!target) return;
      const action = target.dataset["action"];
      const id = target.dataset["id"];
      if (!action || !id) return;
      const booking = ctx.project.bookings.find((b) => b.id === id);
      if (!booking) return;

      switch (action) {
        case "view-notes":
          ctx.showToast(
            booking.notes && booking.notes.trim() !== ""
              ? booking.notes
              : "No notes were saved for this appointment.",
            "info",
          );
          break;
        case "reschedule":
          // Navigate to messages so the customer can ask for a new slot
          // (platform-only messaging — [[project-platform-messaging-only]]).
          ctx.showToast(
            "Send your retailer a message and we'll find a new slot.",
            "info",
          );
          window.dispatchEvent(
            new CustomEvent("cust-nav", { detail: { page: "messages" } }),
          );
          break;
        case "confirm": {
          // Confirm a pending appointment — server marks it confirmed and lands
          // it in the retailer's Bookings section. Refresh picks up the new
          // status (status change survives the auto-refresh signature normaliser).
          target.setAttribute("disabled", "true");
          void (async () => {
            try {
              const r = await ctx.api<{ ok?: boolean }>(
                `/customer/appointments/${encodeURIComponent(id)}/confirm`,
                { method: "POST" },
              );
              if (r && r.ok) {
                ctx.showToast("Appointment confirmed.", "success");
                await ctx.refresh();
              } else {
                target.removeAttribute("disabled");
                ctx.showToast("Couldn't confirm — please try again.", "error");
              }
            } catch {
              target.removeAttribute("disabled");
              ctx.showToast("Couldn't confirm — please try again.", "error");
            }
          })();
          break;
        }
      }
    };
    container.addEventListener("click", onActionClick);

    render();

    return () => {
      disposed = true;
      ac.abort();
      container.removeEventListener("click", onActionClick);
      container.replaceChildren();
    };
  },
};

export default section;
